import React, { useState, useMemo } from 'react';
import { Table, Button, Popconfirm, Input, Space, Tag, Typography, Drawer, Descriptions, List, Divider, Empty } from 'antd';
import { usePurchase } from '../../store/purchaseContext';
import { useProduct } from '../../store/productContext';
import { useSupplier } from '../../store/supplierContext';
import { PurchaseInvoice, PurchaseItem } from '../../types/purchase';
import { EditOutlined, DeleteOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import InvoicePrintHandler from "../../components/Print/InvoicePrintHandler";
import { useTranslation } from "react-i18next";
import PageNavigation from "../Layout/PageNavigation";

const { Text } = Typography;

function getSupplierName(supplierId: any, suppliers: any[]): string {
    if (!supplierId) return '-';
    if (typeof supplierId === 'object') return supplierId.name || '-';
    const supplier = suppliers.find(s => s.id === supplierId || s._id === supplierId);
    return supplier ? supplier.name : '-';
}
function getProductName(productId: any, products: any[]): string {
    if (!productId) return '-';
    if (typeof productId === 'object') return productId.name || '-';
    const product = products.find(p => p.id === productId || (p as any)._id === productId);
    return product ? product.name : '-';
}
function formatDate(date: string) {
    return date ? new Date(date).toLocaleDateString() : '-';
}
function formatCurrency(val: number, currency: string) {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
        }).format(val);
    } catch {
        return val;
    }
}

interface PurchaseListProps {
    onEdit: (purchase: PurchaseInvoice) => void;
}

const PurchaseList: React.FC<PurchaseListProps> = ({ onEdit }) => {
    const { t } = useTranslation();
    const { purchases, deletePurchase } = usePurchase();
    const { products } = useProduct();
    const { suppliers } = useSupplier();
    const [search, setSearch] = useState<string>('');
    const [selectedPurchase, setSelectedPurchase] = useState<PurchaseInvoice | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [printInvoice, setPrintInvoice] = useState<PurchaseInvoice | null>(null);

    const filteredPurchases = useMemo(() => {
        const q = search.toLowerCase();
        return purchases.filter(p => {
            const invoice = (p.invoiceNumber ?? '').toLowerCase();
            const supplierName = getSupplierName(p.supplier, suppliers).toLowerCase();
            return (
                invoice.includes(q) ||
                supplierName.includes(q) ||
                (p.currency ?? '').toLowerCase().includes(q) ||
                String(p.total ?? '').includes(q) ||
                (p.note ?? '').toLowerCase().includes(q) ||
                (p.date ? new Date(p.date).toLocaleDateString() : '').includes(q)
            );
        });
    }, [purchases, search, suppliers]);

    const expandedRowRender = (record: PurchaseInvoice) => (
        <List
            itemLayout="horizontal"
            dataSource={record.items}
            renderItem={(item: PurchaseItem, idx) => (
                <List.Item>
                    <List.Item.Meta
                        title={
                            <span>
                                <b>{getProductName(item.product, products)}</b> <Tag>{item.unit}</Tag>
                            </span>
                        }
                        description={
                            <>
                                <Text type="secondary">{t("qty") || "Qty"}:</Text> {item.qty} &nbsp; | &nbsp;
                                <Text type="secondary">{t("buy_price") || "Buy Price"}:</Text> {formatCurrency(item.buyPrice, record.currency)}
                                {(item.unit === "meter" || item.unit === "roll") && item.unitLength !== undefined && (
                                    <> &nbsp; | &nbsp;<Text type="secondary">{t("length") || "Length"}:</Text> {item.unitLength}</>
                                )}
                            </>
                        }
                    />
                </List.Item>
            )}
            locale={{ emptyText: <Empty description={t("no_items") || "No items"} /> }}
        />
    );

    const openDrawer = (purchase: PurchaseInvoice) => {
        setSelectedPurchase(purchase);
        setDrawerOpen(true);
    };
    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedPurchase(null);
    };

    const columns = [
        {
            title: t("invoice_number") || 'Invoice',
            dataIndex: 'invoiceNumber',
            render: (text: string, record: PurchaseInvoice) => (
                <Button
                    type="link"
                    onClick={() => openDrawer(record)}
                    icon={<EyeOutlined />}
                    style={{ padding: 0 }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t("supplier") || 'Supplier',
            dataIndex: 'supplier',
            render: (val: string) => getSupplierName(val, suppliers),
        },
        {
            title: t("date") || 'Date',
            dataIndex: 'date',
            render: (d: string) => formatDate(d),
        },
        {
            title: t("currency") || 'Currency',
            dataIndex: 'currency',
            render: (cur: string) => (
                <Tag color={cur === 'USD' ? 'blue' : 'gold'}>{cur}</Tag>
            ),
        },
        {
            title: t("total") || 'Total',
            dataIndex: 'total',
            render: (val: number, rec: PurchaseInvoice) => formatCurrency(val, rec.currency),
        },
        {
            title: t("paid") || 'Paid',
            dataIndex: 'paid',
            render: (val: number, rec: PurchaseInvoice) => formatCurrency(val, rec.currency),
        },
        {
            title: t("remain") || 'Remain',
            dataIndex: 'remain',
            render: (val: number, rec: PurchaseInvoice) => formatCurrency(val, rec.currency),
        },
        {
            title: t("actions") || 'Actions',
            render: (_: any, record: PurchaseInvoice) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEdit(record)}
                    />
                    <Popconfirm
                        title={t("are_you_sure_delete") || "Are you sure to delete?"}
                        onConfirm={() => deletePurchase(record.id!)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                    <Button
                        icon={<PrinterOutlined />}
                        size="small"
                        onClick={() => setPrintInvoice(record)}
                    >
                        {t("print") || "Print"}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ background: "#fff", borderRadius: 12, padding: 18, boxShadow: "0 2px 8px #f0f1f2" }}>
            <PageNavigation title={t("purchase_list") as string} />
            <Space style={{ marginBottom: 12 }}>
                <Input.Search
                    allowClear
                    style={{ maxWidth: 300 }}
                    placeholder={t("search_invoice_supplier") || "Search by invoice or supplier"}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </Space>
            <Table
                rowKey={record => record.id as string}
                dataSource={filteredPurchases}
                columns={columns}
                expandable={{ expandedRowRender }}
                pagination={{ pageSize: 8 }}
                size="middle"
            />

            <Drawer
                title={
                    selectedPurchase
                        ? `${t("purchase")} ${selectedPurchase.invoiceNumber}`
                        : t("purchase_details") || 'Purchase Details'
                }
                placement="right"
                onClose={closeDrawer}
                open={drawerOpen}
                width={480}
            >
                {selectedPurchase ? (
                    <>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label={t("supplier") || "Supplier"}>
                                {getSupplierName(selectedPurchase.supplier, suppliers)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("date") || "Date"}>
                                {formatDate(selectedPurchase.date)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("currency") || "Currency"}>
                                <Tag color={selectedPurchase.currency === 'USD' ? 'blue' : 'gold'}>
                                    {selectedPurchase.currency}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("total") || "Total"}>
                                {formatCurrency(selectedPurchase.total, selectedPurchase.currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("paid") || "Paid"}>
                                {formatCurrency(selectedPurchase.paid, selectedPurchase.currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("remain") || "Remain"}>
                                {formatCurrency(selectedPurchase.remain, selectedPurchase.currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("note") || "Note"}>
                                {selectedPurchase.note || '-'}
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider orientation="left" style={{ marginTop: 16 }}>
                            {t("purchase_items") || "Purchase Items"}
                        </Divider>
                        {expandedRowRender(selectedPurchase)}
                    </>
                ) : (
                        <Empty description={t("no_purchase_selected") || "No purchase selected"} />
                )}
            </Drawer>
            {/* Print Handler */}
            {printInvoice && (
                <InvoicePrintHandler
                    invoice={printInvoice}
                    type="purchase"
                    getCustomerOrSupplierName={() => getSupplierName(printInvoice.supplier, suppliers)}
                    getProductName={(id) => getProductName(id, products)}
                    onAfterPrint={() => setPrintInvoice(null)}
                />
            )}
        </div>
    );
};

export default PurchaseList;
